import { useCallback, useEffect, useRef, useState } from "react";
import { Platform } from "react-native";

/**
 * Tucks the quick access menu out of the way while a member is working.
 *
 * The menu is two rows tall once you count the labels, and on a phone that is a
 * real bite out of the screen for something you only touch when you are leaving
 * the page you are on. So it hides itself fully. The member gets it back by
 * tapping the orange title, pulling down a short way, or scrolling up.
 * Tapping the title again hides it. There is no hover reveal — reaching for
 * mail or the bell must not drop the menu and shove those icons away.
 *
 * The order of events matters more than the animation does:
 *
 *   First load  — the row plays its existing drift so every icon is seen, then
 *                 tucks itself away. Hiding a menu nobody has looked at yet
 *                 just makes the app look like it has no navigation.
 *   After that  — it starts tucked, with no drift. Once is teaching; every load
 *                 is nagging.
 *
 * "First load" is remembered in sessionStorage, so it survives reloads — a member
 * who refreshes has not forgotten what they saw a minute ago — but returns when
 * they come back to the app fresh. localStorage would mean once per browser for
 * good, which makes the cue unrepeatable for anyone who missed it the first time.
 */

/**
 * Hidden height. A half-visible sliver read as a rendering fault, so the row
 * tucks all the way away. It comes back on a header tap or a short pull-down.
 */
export const SLIVER_HEIGHT = 0;

/** Kept so existing imports do not break. The row no longer slides inside a sliver. */
export const TUCK_SHIFT = 0;

/** Short pull at the top of the page that brings the menu back. */
const PULL_REVEAL = 36;

/** Above this width the screen has room to spare, so nothing is hidden. */
export const PHONE_MAX_WIDTH = 768;

/**
 * Scroll travel needed before the menu reacts.
 *
 * Reacting to every pixel makes the bar flicker, because ordinary reading is
 * full of tiny scroll corrections in both directions. Travel is accumulated in
 * one direction and reset when the member turns around, so this is a deliberate
 * change of direction rather than a jitter.
 */
const SCROLL_THRESHOLD = 12;

/** Pause between the drift finishing and the tuck, so the row is read before it goes. */
const INTRO_SETTLE = 600;

/**
 * How long scroll input is re-baselined but not acted on after the row moves.
 *
 * Tucking the row makes the scrolling area taller, and a page sitting near its
 * bottom answers that by clamping its own scroll position. That clamp arrives as
 * a genuine upward scroll, which reads as "reveal", which shortens the area
 * again — the row flaps open and shut on its own. Positions are still tracked
 * through the lock, so this drops the phantom movement rather than the member's.
 */
const SETTLE_LOCK = 360;

const INTRO_STORAGE_KEY = "oe.navIntroSeen.v1";

/**
 * Web only. The reveal is driven by a capture-phase scroll listener, which is a
 * DOM technique: every screen owns its own ScrollView, so there is no single
 * scroll position to watch, but a capture listener on `document` hears all of
 * them without every screen having to report in. On native the menu stays put.
 */
const isWeb = Platform.OS === "web";

const getIsPhone = () =>
  isWeb && typeof window !== "undefined"
    ? window.innerWidth <= PHONE_MAX_WIDTH
    : false;

const readIntroSeen = () => {
  if (!isWeb || typeof window === "undefined") return false;
  try {
    return window.sessionStorage.getItem(INTRO_STORAGE_KEY) === "1";
  } catch {
    // Private browsing can refuse storage. Replaying the drift is a far smaller
    // problem than throwing on render, so treat it as unseen.
    return false;
  }
};

const markIntroSeen = () => {
  if (!isWeb || typeof window === "undefined") return;
  try {
    window.sessionStorage.setItem(INTRO_STORAGE_KEY, "1");
  } catch {
    // Nothing to do: the drift simply plays again next time.
  }
};

/**
 * @param {object}  options
 * @param {string}  options.activeTab  current screen, so a navigation re-tucks the row
 * @param {boolean} options.enabled    false pins the row open regardless of width
 * @returns {{
 *   collapsed: boolean,
 *   playHint: boolean,
 *   reveal: () => void,
 *   onHintComplete: () => void,
 * }}
 */
export default function useNavAutoHide({ activeTab, enabled = true } = {}) {
  const [isPhone, setIsPhone] = useState(getIsPhone);
  const [introDone, setIntroDone] = useState(readIntroSeen);
  const [collapsed, setCollapsed] = useState(
    () => enabled && getIsPhone() && readIntroSeen(),
  );

  const autoHide = isWeb && isPhone && enabled;

  // Read inside callbacks that must not be rebuilt when this flips.
  const autoHideRef = useRef(autoHide);
  autoHideRef.current = autoHide;

  const settleTimer = useRef(null);
  const manualIntroToggle = useRef(false);

  // Shared with the scroll listener so that every way of moving the row — the
  // intro tuck, a navigation, a header tap — suppresses the layout shift
  // it causes, not just the ones the listener itself started.
  const lockedUntil = useRef(0);
  const lockScroll = useCallback(() => {
    lockedUntil.current = Date.now() + SETTLE_LOCK;
  }, []);

  useEffect(() => {
    if (!isWeb || typeof window === "undefined") return undefined;

    const onResize = () => setIsPhone(window.innerWidth <= PHONE_MAX_WIDTH);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // Rotating to a wide screen, or opening on a desktop, must give the row back
  // rather than leave a sliver on a screen with room for the whole thing.
  useEffect(() => {
    if (!autoHide) setCollapsed(false);
  }, [autoHide]);

  // The drift has finished, so the member has now seen the row: tuck it, and
  // remember not to introduce it again.
  const onHintComplete = useCallback(() => {
    if (!autoHideRef.current || introDone) return;

    markIntroSeen();
    settleTimer.current = setTimeout(() => {
      setIntroDone(true);
      setCollapsed(true);
      lockScroll();
    }, INTRO_SETTLE);
  }, [introDone, lockScroll]);

  useEffect(
    () => () => {
      if (settleTimer.current) clearTimeout(settleTimer.current);
    },
    [],
  );

  // Leaving a screen tucks the row again, so the member arrives with the space
  // given to the page rather than to the menu they have just finished using.
  useEffect(() => {
    if (!autoHide || !introDone) return;
    if (manualIntroToggle.current) {manualIntroToggle.current = false; return;}
    setCollapsed(true);
    lockScroll();
  }, [activeTab, autoHide, introDone, lockScroll]);

  useEffect(() => {
    if (!autoHide || !introDone) return undefined;
    if (typeof document === "undefined") return undefined;

    // Each scroller is tracked separately: a screen can hold several, and
    // comparing one's position against another's produces phantom jumps.
    const lastTop = new WeakMap();
    let travelled = 0;

    const onScroll = (event) => {
      const target =
        event.target === document || event.target === window
          ? document.documentElement
          : event.target;
      if (!target || typeof target.scrollTop !== "number") return;

      const top = target.scrollTop;
      const previous = lastTop.has(target) ? lastTop.get(target) : top;
      // Recorded before the lock is checked, so the position the member is left
      // at is always the one the next real gesture is measured from.
      lastTop.set(target, top);

      if (Date.now() < lockedUntil.current) return;

      const delta = top - previous;
      // The menu's own row scrolls sideways, which fires scroll events with an
      // unchanged scrollTop. Those must not count as anything.
      if (delta === 0) return;

      // Turning around discards the travel built up the other way, so the
      // threshold measures this movement rather than the whole session.
      if (travelled !== 0 && Math.sign(delta) !== Math.sign(travelled)) {
        travelled = 0;
      }
      travelled += delta;

      if (travelled > SCROLL_THRESHOLD) {
        setCollapsed(true);
        travelled = 0;
        lockScroll();
      } else if (travelled < -SCROLL_THRESHOLD) {
        setCollapsed(false);
        travelled = 0;
        lockScroll();
      }
    };

    document.addEventListener("scroll", onScroll, true);
    return () => document.removeEventListener("scroll", onScroll, true);
  }, [autoHide, introDone, lockScroll]);

  const reveal = useCallback(() => {
    setCollapsed(false);
    lockScroll();
  }, [lockScroll]);

  const toggle = useCallback(() => {
    setCollapsed((was) => !was);
    lockScroll();
  }, [lockScroll]);

  // A labelled control can toggle the row on any screen size, including before
  // the introductory animation has completed. Exclude it from header-click
  // capture so a single tap never toggles twice.
  useEffect(() => {
    if (!isWeb || !enabled || typeof window === "undefined") return undefined;
    const onToggle = () => {
      if (settleTimer.current) clearTimeout(settleTimer.current);
      if (!introDone) {
        manualIntroToggle.current = true;
        markIntroSeen();
        setIntroDone(true);
      }
      toggle();
    };
    window.addEventListener("oe-nav-toggle", onToggle);
    return () => window.removeEventListener("oe-nav-toggle", onToggle);
  }, [enabled, introDone, toggle]);

  const isRevealNode = (node) =>
    node?.id === "oe-app-header" ||
    node?.id === "oe-nav-reveal" ||
    node?.classList?.contains?.("oe-nav-reveal") ||
    node?.dataset?.navReveal === "true" ||
    node?.getAttribute?.("data-nav-reveal") === "true";

  const isAccountNode = (node) =>
    node?.id === "oe-header-account" ||
    node?.classList?.contains?.("oe-header-account") ||
    node?.dataset?.navIgnore === "true" ||
    node?.getAttribute?.("data-nav-ignore") === "true";

  const pathIgnoresNav = (path) => path.some(isAccountNode);

  const accountColumn = (event) => {
    const el =
      typeof document !== "undefined"
        ? document.getElementById("oe-header-account") ||
          document.querySelector(".oe-header-account")
        : null;
    if (!el || typeof event.clientX !== "number") return false;
    const box = el.getBoundingClientRect();
    return event.clientX >= box.left - 16 && event.clientX <= box.right + 16;
  };

  const headerHit = (event) => {
    const path = typeof event.composedPath === "function" ? event.composedPath() : [];
    // Click the orange bar to toggle. Mail, bell, and profile stay out of it
    // so those icons do not move out from under the pointer.
    if (pathIgnoresNav(path) || accountColumn(event)) return false;
    return path.some(isRevealNode);
  };

  useEffect(() => {
    if (!isWeb || !enabled || typeof document === "undefined") return undefined;

    const onHeaderClick = (event) => {
      if (!headerHit(event)) return;
      window.dispatchEvent(new CustomEvent("oe-nav-toggle"));
    };

    document.addEventListener("click", onHeaderClick, true);
    return () => {
      document.removeEventListener("click", onHeaderClick, true);
    };
  }, [enabled]);

  useEffect(() => {
    if (!autoHide || !introDone) return undefined;
    if (typeof document === "undefined") return undefined;

    let startY = 0;
    let pulling = false;

    const scrollerTop = (el) => {
      let node = el;
      while (node && node !== document.body && node !== document.documentElement) {
        if (node.scrollHeight > node.clientHeight + 2) return node.scrollTop;
        node = node.parentElement;
      }
      return window.scrollY || document.documentElement.scrollTop || 0;
    };

    const onTouchStart = (event) => {
      const touch = event.touches?.[0];
      if (!touch) return;
      pulling = scrollerTop(event.target) <= 0;
      startY = touch.clientY;
    };

    const onTouchMove = (event) => {
      if (!pulling || !autoHideRef.current) return;
      const touch = event.touches?.[0];
      if (!touch) return;
      const delta = touch.clientY - startY;
      if (delta < 8 || scrollerTop(event.target) > 0) return;

      // First short pull while hidden: show the menu and hold the refresh.
      // Once the menu is open, we stop intercepting so a longer / second pull
      // can still reload the page.
      if (collapsed) {
        if (delta >= PULL_REVEAL) {
          reveal();
          pulling = false;
        }
        if (event.cancelable) event.preventDefault();
      }
    };

    const onTouchEnd = () => {
      pulling = false;
    };

    document.addEventListener("touchstart", onTouchStart, { capture: true, passive: true });
    document.addEventListener("touchmove", onTouchMove, { capture: true, passive: false });
    document.addEventListener("touchend", onTouchEnd, { capture: true, passive: true });
    return () => {
      document.removeEventListener("touchstart", onTouchStart, true);
      document.removeEventListener("touchmove", onTouchMove, true);
      document.removeEventListener("touchend", onTouchEnd, true);
    };
  }, [autoHide, introDone, collapsed, reveal]);

  return {
    collapsed,
    // Desktop keeps the drift on every load, as it always has: there is nothing
    // to teach about a menu that never hides.
    playHint: autoHide ? !introDone : true,
    reveal,
    onHintComplete,
  };
}
